import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  SearchIcon,
  CartIcon,
  CheckIcon,
  ArrowRightIcon,
  AlertCircleIcon,
  CloseIcon,
} from './SvgIcons';
import CreditsUsageOverview from 'pages/credit-plans/CreditsUsageOverview';
import LicensesAPI from 'api/LicensesAPI';
import PaymentsAPI from 'api/PaymentsAPI';
import {
  buildLicenseInventory,
  selectTeamSubscriptions,
} from 'utils/licenseInventory';
import { getPrimaryTeamAdmin } from 'utils/teamAdmin';

interface TeamMember {
  id: string;
  name: string;
  email: string;
  license: LicenseType;
  role: 'admin' | 'member';
  /** Licence comes from the customer's identity provider; not editable here. */
  scimManaged?: boolean;
}

/* ─── License Config (self-contained) ─── */
const ITEMS_PER_PAGE = 10;
type LicenseType = 'standard' | 'pro' | 'business' | 'enterprise' | 'none';

const licenseConfig: Record<
  LicenseType,
  { label: string; dotClass: string; badgeClass: string }
> = {
  standard: {
    label: 'Standard',
    dotClass: 'asgn-dot asgn-dot-standard',
    badgeClass: 'asgn-badge asgn-badge-standard',
  },
  pro: {
    label: 'Pro',
    dotClass: 'asgn-dot asgn-dot-pro',
    badgeClass: 'asgn-badge asgn-badge-pro',
  },
  business: {
    label: 'Business',
    dotClass: 'asgn-dot asgn-dot-business',
    badgeClass: 'asgn-badge asgn-badge-business',
  },
  enterprise: {
    label: 'Enterprise',
    dotClass: 'asgn-dot asgn-dot-enterprise',
    badgeClass: 'asgn-badge asgn-badge-enterprise',
  },
  none: {
    label: 'No License',
    dotClass: 'asgn-dot asgn-dot-none',
    badgeClass: 'asgn-badge asgn-badge-none',
  },
};

const licenseOptions: LicenseType[] = [
  'standard',
  'pro',
  'business',
  'enterprise',
];

interface LicenseCentricAssignerProps {
  cur_user: any;
  team: any;
  handleTabChange: any;
  refreshSelectedTeamLicenseState: any;
}

const LicenseCentricAssigner: React.FC<LicenseCentricAssignerProps> = ({
  cur_user,
  team,
  handleTabChange,
  refreshSelectedTeamLicenseState,
}) => {
  const location = useLocation();
  const history = useHistory();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  // Stripe customers behind the team's seats. Sent with an assign so the server
  // can read their subscriptions directly instead of relying on Stripe's search
  // index, which can miss seats bought moments ago.
  const [billingCustomerIds, setBillingCustomerIds] = useState<string[]>([]);
  const [licenseInventory, setLicenseInventory] = useState<
    Record<LicenseType, { total: number; used: number }>
  >({} as Record<LicenseType, { total: number; used: number }>);
  const [selectedLicense, setSelectedLicense] = useState<LicenseType | null>(
    null,
  );
  const [selectedMemberIds, setSelectedMemberIds] = useState<Set<string>>(
    new Set(),
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [isApplying, setIsApplying] = useState(false);
  const [toastMsg, setToastMsg] = useState<{
    type: 'success' | 'error';
    title: string;
    description: string;
  } | null>(null);

  useEffect(() => {
    if (!toastMsg) return;
    const timer = setTimeout(() => setToastMsg(null), 5000);
    // eslint-disable-next-line consistent-return
    return () => clearTimeout(timer);
  }, [toastMsg]);

  useEffect(() => {
    const fetchLicenses = async () => {
      if (!team?.id) return;

      // Seat totals come from Stripe, looked up by the team's billing contact.
      // `team_admin` only reaches the client through the extra /groups/batch
      // call at login — Keycloak returns a user's groups without attributes —
      // so a team whose attribute never arrived, or that carries the [' ']
      // placeholder this codebase writes when it empties an attribute array,
      // skipped the lookup entirely and reported zero seats on every tier.
      // That disables every tile, blocking assignment of licences the team has
      // actually paid for, while the Team and Subscriptions tabs show them
      // correctly because both already fall back to the signed-in user. Do the
      // same here rather than leaving this one surface without a fallback.
      const adminEmail = getPrimaryTeamAdmin(cur_user?.selected_team)?.trim();
      const signedInEmail = cur_user?.basics?.email;
      const billingLookupEmail = adminEmail || signedInEmail;

      const [licenseRes, customerRes] = await Promise.all([
        LicensesAPI.getLicensesForTeam(team.id),
        billingLookupEmail
          ? PaymentsAPI.getCustomerByEmail(billingLookupEmail)
          : Promise.resolve(null),
      ]);

      // Non-fatal. Returning here left `licenseInventory` at its initial empty
      // state, which renders as "0 available" on every tier and disables every
      // tile — so a failure to read *assignments* also took out the *totals*,
      // which come from Stripe and were fetched independently. Carry on with no
      // assignment data: `used` then reads low, and the server re-checks seat
      // capacity on assign, so an over-count is refused there rather than
      // over-allocating.
      if (!licenseRes?.success) {
        setToastMsg({
          type: 'error',
          title: 'Error fetching licenses',
          description: `${
            licenseRes?.result || 'Unable to load current assignments.'
          } Assigned counts may be understated.`,
        });
      }

      const assignmentList = Array.isArray(licenseRes?.result)
        ? licenseRes.result
        : [];
      const teamMembers = Array.isArray(team?.members) ? team.members : [];
      const adminEmails = new Set(team?.attributes?.team_admin || []);

      const customersFrom = (response: any): any[] =>
        Array.isArray(response?.result?.data) ? response.result.data : [];

      let customerLookup = customerRes;
      let customers = customersFrom(customerLookup);
      let subscriptions = selectTeamSubscriptions(customers, team);

      // `team_admin` is multi-valued and grantAdmin appends to it, so index 0
      // can be a former admin with no Stripe customer of their own. That lookup
      // *succeeds* and simply matches nothing, which reported zero seats on
      // every tier with no error to explain it. Retry as the signed-in user
      // before concluding the team has bought nothing.
      if (
        subscriptions.length === 0 &&
        signedInEmail &&
        signedInEmail !== billingLookupEmail
      ) {
        customerLookup = await PaymentsAPI.getCustomerByEmail(signedInEmail);
        customers = customersFrom(customerLookup);
        subscriptions = selectTeamSubscriptions(customers, team);
      }

      const activeSubscriptions = subscriptions.filter((subscription: any) =>
        ['active', 'trialing', 'past_due'].includes(subscription?.status),
      );

      const licenseTotals = buildLicenseInventory(activeSubscriptions);
      const inv = {
        standard: { total: licenseTotals.standard, used: 0 },
        pro: { total: licenseTotals.pro, used: 0 },
        business: { total: licenseTotals.business, used: 0 },
        enterprise: { total: licenseTotals.enterprise, used: 0 },
        none: { total: Infinity, used: 0 },
      };

      assignmentList.forEach((assignment: any) => {
        const lic = assignment?.currentLicense as LicenseType;
        if (lic && lic !== 'none' && inv[lic]) inv[lic].used += 1;
      });

      const assignmentMap = new Map(
        assignmentList.map((assignment: any) => [
          String(assignment?.memberId),
          assignment?.currentLicense as LicenseType,
        ]),
      );

      // Members whose licence is driven by the customer's identity provider.
      // Assigning to them here would be replaced at their next sign-in, so the
      // controls are disabled rather than allowed to look like they worked.
      const scimManagedIds = new Set(
        assignmentList
          .filter((assignment: any) => assignment?.scimManaged)
          .map((assignment: any) => String(assignment?.memberId)),
      );

      const normalizedMembers: TeamMember[] = teamMembers.map(
        (member: any, index: number) => ({
          id: String(member.id ?? member.email ?? index),
          name:
            `${member.firstName || ''} ${member.lastName || ''}`.trim() ||
            member.email ||
            'Unknown User',
          email: member.email || '',
          license: assignmentMap.get(String(member.id)) || 'none',
          role: adminEmails.has(member.email) ? 'admin' : 'member',
          loginsThisMonth: 0,
          scimManaged: scimManagedIds.has(String(member.id)),
        }),
      );

      setLicenseInventory(inv);
      setBillingCustomerIds(
        Array.from(
          new Set(
            subscriptions
              .map((subscription: any) =>
                typeof subscription?.customer === 'string'
                  ? subscription.customer
                  : subscription?.customer?.id,
              )
              .filter(Boolean),
          ),
        ) as string[],
      );
      setMembers(normalizedMembers);

      if (billingLookupEmail && !customerLookup?.success) {
        setToastMsg({
          type: 'error',
          title: 'Error fetching license totals',
          description:
            customerLookup?.result || 'Unable to load latest totals.',
        });
      } else if (
        billingLookupEmail &&
        customers.length > 0 &&
        subscriptions.length === 0
      ) {
        // Billing exists for this address but none of it maps to this team, so
        // every tier reads zero. A team that has simply bought nothing returns
        // no customers at all and is left alone — it needs no explanation.
        setToastMsg({
          type: 'error',
          title: 'No subscription found for this team',
          description: `Stripe has billing under ${billingLookupEmail}, but nothing attributable to "${team?.name}", so purchased seats cannot be counted.`,
        });
      }
    };
    fetchLicenses();
  }, [team, cur_user?.basics?.email]);

  const filteredMembers = members.filter(
    (m) =>
      m.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      m.email.toLowerCase().includes(searchQuery.toLowerCase()),
  );
  const totalPages = Math.ceil(filteredMembers.length / ITEMS_PER_PAGE);
  const paginatedMembers = filteredMembers.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  useEffect(() => {
    setCurrentPage(1);
    setSelectedLicense(null);
    setSelectedMemberIds(new Set());
  }, [team]);

  const handleLicenseSelect = (license: LicenseType) => {
    if (selectedLicense === license) {
      setSelectedLicense(null);
      setSelectedMemberIds(new Set());
    } else {
      setSelectedLicense(license);
      setSelectedMemberIds(
        new Set(members.filter((m) => m.license === license).map((m) => m.id)),
      );
    }
  };

  const handleMemberToggle = (memberId: string) => {
    if (members.find((member) => member.id === memberId)?.scimManaged) {
      return;
    }
    const newSelected = new Set(selectedMemberIds);
    if (newSelected.has(memberId)) newSelected.delete(memberId);
    else newSelected.add(memberId);
    setSelectedMemberIds(newSelected);
  };

  // Managed members are never selectable, so "select all" means every member
  // that can actually be changed. The button label compares against this same
  // count, or it could never reach its "all selected" state.
  const selectableMembers = filteredMembers.filter((m) => !m.scimManaged);
  const selectableCount = selectableMembers.length;

  const handleSelectAll = () => {
    if (selectableCount > 0 && selectedMemberIds.size === selectableCount)
      setSelectedMemberIds(new Set());
    else setSelectedMemberIds(new Set(selectableMembers.map((m) => m.id)));
  };

  const handleApply = async () => {
    if (!selectedLicense || !team?.id || isApplying) return;

    const inv = licenseInventory[selectedLicense] || { total: 0, used: 0 };
    const currentWithLicense = members.filter(
      (m) => m.license === selectedLicense,
    );
    const currentIds = new Set(currentWithLicense.map((m) => m.id));
    const toAdd = [...selectedMemberIds].filter((id) => !currentIds.has(id));
    const toRemove = currentWithLicense.filter(
      (m) => !selectedMemberIds.has(m.id),
    );
    const netChange = toAdd.length - toRemove.length;
    const newUsed = inv.used + netChange;

    if (newUsed > inv.total) {
      setToastMsg({
        type: 'error',
        title: `Not enough ${licenseConfig[selectedLicense].label} licenses`,
        description: `You need ${newUsed} but only have ${inv.total}. Purchase more licenses to continue.`,
      });
      return;
    }

    const memberById = new Map(members.map((member) => [member.id, member]));
    const changes = [
      ...toAdd
        .map((memberId) => {
          const member = memberById.get(memberId);
          if (!member || member.license === selectedLicense) return null;
          return { memberId: memberId, license: selectedLicense };
        })
        .filter(Boolean),
      ...toRemove
        .map((member) => {
          if (member.license === 'none') return null;
          return { memberId: member.id, license: 'none' as LicenseType };
        })
        .filter(Boolean),
    ] as { memberId: string; license: LicenseType }[];

    if (changes.length === 0) {
      setSelectedLicense(null);
      setSelectedMemberIds(new Set());
      return;
    }

    setIsApplying(true);

    try {
      const resp = await LicensesAPI.assignLicenses(team.id, {
        adminId: String(cur_user?.basics?.id || cur_user?.basics?.email || ''),
        changes: changes,
        customerIds: billingCustomerIds,
      });

      if (!resp?.success) {
        setToastMsg({
          type: 'error',
          title: 'Error assigning licenses',
          description: resp?.result || 'Unable to apply license changes.',
        });
        return;
      }

      const inventoryDelta: Partial<Record<LicenseType, number>> = {};

      [...selectedMemberIds].forEach((memberId) => {
        const currentLicense = memberById.get(memberId)?.license || 'none';
        if (currentLicense === selectedLicense) return;

        if (currentLicense !== 'none') {
          inventoryDelta[currentLicense] =
            (inventoryDelta[currentLicense] || 0) - 1;
        }

        inventoryDelta[selectedLicense] =
          (inventoryDelta[selectedLicense] || 0) + 1;
      });

      toRemove.forEach((member) => {
        if (member.license === 'none') return;
        inventoryDelta[member.license] =
          (inventoryDelta[member.license] || 0) - 1;
      });

      // optimistic UI update for team members
      setMembers((prev) =>
        prev.map((member) => {
          if (selectedMemberIds.has(member.id)) {
            return { ...member, license: selectedLicense };
          }
          if (currentIds.has(member.id) && !selectedMemberIds.has(member.id)) {
            return { ...member, license: 'none' };
          }
          return member;
        }),
      );

      setLicenseInventory((prev) => ({
        ...prev,
        ...Object.entries(inventoryDelta).reduce((acc, [license, delta]) => {
          const typedLicense = license as LicenseType;
          const current = prev[typedLicense] || { total: 0, used: 0 };
          acc[typedLicense] = {
            ...current,
            used: Math.max(0, current.used + (delta || 0)),
          };
          return acc;
        }, {} as Record<LicenseType, { total: number; used: number }>),
      }));

      // refresh global current-user state in case the logged-in user was affected
      await refreshSelectedTeamLicenseState();

      // optional but recommended if you also track role/license maps locally:
      // await fetchLicenses();

      setSelectedLicense(null);
      setSelectedMemberIds(new Set());
      setToastMsg({
        type: 'success',
        title: 'License changes applied',
        description: 'Your license assignments were saved successfully.',
      });
    } finally {
      setIsApplying(false);
    }
  };

  // const handleApply = async () => {
  //   if (!selectedLicense || !team?.id || isApplying) return;

  //   const inv = licenseInventory[selectedLicense] || { total: 0, used: 0 };
  //   const currentWithLicense = members.filter(
  //     (m) => m.license === selectedLicense,
  //   );
  //   const currentIds = new Set(currentWithLicense.map((m) => m.id));
  //   const toAdd = [...selectedMemberIds].filter((id) => !currentIds.has(id));
  //   const toRemove = currentWithLicense.filter(
  //     (m) => !selectedMemberIds.has(m.id),
  //   );
  //   const netChange = toAdd.length - toRemove.length;
  //   const newUsed = inv.used + netChange;

  //   if (newUsed > inv.total) {
  //     setToastMsg({
  //       type: 'error',
  //       title: `Not enough ${licenseConfig[selectedLicense].label} licenses`,
  //       description: `You need ${newUsed} but only have ${inv.total}. Purchase more licenses to continue.`,
  //     });
  //     return;
  //   }

  //   const memberById = new Map(members.map((member) => [member.id, member]));
  //   const changes = [
  //     ...toAdd
  //       .map((memberId) => {
  //         const member = memberById.get(memberId);
  //         if (!member || member.license === selectedLicense) return null;
  //         return { memberId: memberId, license: selectedLicense };
  //       })
  //       .filter(Boolean),
  //     ...toRemove
  //       .map((member) => {
  //         if (member.license === 'none') return null;
  //         return { memberId: member.id, license: 'none' as LicenseType };
  //       })
  //       .filter(Boolean),
  //   ] as { memberId: string; license: LicenseType }[];

  //   if (changes.length === 0) {
  //     setSelectedLicense(null);
  //     setSelectedMemberIds(new Set());
  //     return;
  //   }

  //   setIsApplying(true);
  //   const resp = await LicensesAPI.assignLicenses(team.id, {
  //     adminId: String(cur_user?.basics?.id || cur_user?.basics?.email || ''),
  //     changes: changes,
  //   });
  //   setIsApplying(false);

  //   if (!resp?.success) {
  //     setToastMsg({
  //       type: 'error',
  //       title: 'Error assigning licenses',
  //       description: resp?.result || 'Unable to apply license changes.',
  //     });
  //     return;
  //   }

  //   const inventoryDelta: Partial<Record<LicenseType, number>> = {};
  //   [...selectedMemberIds].forEach((memberId) => {
  //     const currentLicense = memberById.get(memberId)?.license || 'none';
  //     if (currentLicense === selectedLicense) return;
  //     if (currentLicense !== 'none')
  //       inventoryDelta[currentLicense] =
  //         (inventoryDelta[currentLicense] || 0) - 1;
  //     inventoryDelta[selectedLicense] =
  //       (inventoryDelta[selectedLicense] || 0) + 1;
  //   });
  //   toRemove.forEach((member) => {
  //     if (member.license === 'none') return;
  //     inventoryDelta[member.license] =
  //       (inventoryDelta[member.license] || 0) - 1;
  //   });

  //   setMembers((prev) =>
  //     prev.map((member) => {
  //       if (selectedMemberIds.has(member.id))
  //         return { ...member, license: selectedLicense };
  //       if (currentIds.has(member.id) && !selectedMemberIds.has(member.id))
  //         return { ...member, license: 'none' };
  //       return member;
  //     }),
  //   );
  //   setLicenseInventory((prev) => ({
  //     ...prev,
  //     ...Object.entries(inventoryDelta).reduce((acc, [license, delta]) => {
  //       const typedLicense = license as LicenseType;
  //       const current = prev[typedLicense] || { total: 0, used: 0 };
  //       acc[typedLicense] = {
  //         ...current,
  //         used: Math.max(0, current.used + (delta || 0)),
  //       };
  //       return acc;
  //     }, {} as Record<LicenseType, { total: number; used: number }>),
  //   }));
  //   setSelectedLicense(null);
  //   setSelectedMemberIds(new Set());
  //   setToastMsg({
  //     type: 'success',
  //     title: 'License changes applied',
  //     description: 'Your license assignments were saved successfully.',
  //   });
  // };

  const pendingChanges = () => {
    if (!selectedLicense) return { add: 0, remove: 0 };
    const currentWithLicense = members.filter(
      (m) => m.license === selectedLicense,
    );
    const currentIds = new Set(currentWithLicense.map((m) => m.id));
    return {
      add: [...selectedMemberIds].filter((id) => !currentIds.has(id)).length,
      remove: currentWithLicense.filter((m) => !selectedMemberIds.has(m.id))
        .length,
    };
  };
  const changes = pendingChanges();

  // "Track Credits" links land here with #credits-section -- scroll the
  // Credits overview into view once the tab has rendered.
  useEffect(() => {
    if (location.hash !== '#credits-section') {
      return undefined;
    }
    const timer = setTimeout(() => {
      document
        .getElementById('credits-section')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 150);
    return () => clearTimeout(timer);
  }, [location.hash]);

  return (
    <div className='asgn-root'>
      {(cur_user.role === 'owner' || cur_user.role === 'admin') && (
        <>
          {toastMsg && (
            <div className='asgn-toast' data-type={toastMsg.type}>
              {toastMsg.type === 'success' ? <CheckIcon /> : <AlertCircleIcon />}
              <div className='asgn-toast-content'>
                <p className='asgn-toast-title'>{toastMsg.title}</p>
                <p className='asgn-toast-desc'>{toastMsg.description}</p>
              </div>
              <button
                className='asgn-toast-close'
                onClick={() => setToastMsg(null)}
              >
                <CloseIcon />
              </button>
            </div>
          )}
          <div className='asgn-header'>
            <div className='asgn-search'>
              <SearchIcon />
              <input
                className='asgn-search-input'
                placeholder='Search users...'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            {cur_user.role === 'owner' && (
              <button
                className='asgn-btn asgn-btn-primary'
                onClick={() => handleTabChange('Subscriptions')}
              >
                <CartIcon /> Buy More Licenses
              </button>
            )}
          </div>

          <div className='asgn-panel'>
            {/* License Selection Panel */}
            <div>
              <h3 className='asgn-step-title'>1. Select License Type</h3>
              <div className='asgn-license-list'>
                {licenseOptions.map((license) => {
                  const config = licenseConfig[license];
                  const inv = licenseInventory[license] || { total: 0, used: 0 };
                  const isSelected = selectedLicense === license;
                  const available = Math.max(0, inv.total - inv.used);
                  return (
                    <button
                      key={license}
                      onClick={() => handleLicenseSelect(license)}
                      disabled={inv.total === 0}
                      className='asgn-license-option'
                      data-selected={isSelected || undefined}
                    >
                      <div className='asgn-license-top'>
                        <div className='asgn-license-label'>
                          <span className={config.dotClass} />
                          <span className='asgn-license-label-text'>
                            {config.label}
                          </span>
                        </div>
                        {isSelected && (
                          <div className='asgn-license-check'>
                            <CheckIcon />
                          </div>
                        )}
                      </div>
                      <div className='asgn-license-stats'>
                        <span>{inv.used} assigned</span>
                        <span
                          className='asgn-license-available'
                          data-empty={available <= 0 || undefined}
                        >
                          {available} available
                        </span>
                      </div>
                      {available <= 0 && inv.total > 0 && (
                        <div className='asgn-license-warning'>
                          <span className='asgn-license-warning-text'>
                            <AlertCircleIcon /> No slots left — buy more
                          </span>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* User Selection Panel */}
            <div>
              <div className='asgn-users-header'>
                <h3 className='asgn-step-title' style={{ marginBottom: 0 }}>
                  2. Select Users to Assign
                </h3>
                {selectedLicense && (
                  <button
                    className='asgn-btn asgn-btn-ghost asgn-btn-sm'
                    onClick={handleSelectAll}
                  >
                    {selectableCount > 0 &&
                    selectedMemberIds.size === selectableCount
                      ? 'Deselect All'
                      : 'Select All'}
                  </button>
                )}
              </div>

              {!selectedLicense ? (
                <div className='asgn-empty'>
                  <ArrowRightIcon />
                  <p>
                    Select a license type from the left to begin assigning users
                  </p>
                </div>
              ) : (
                <div className='asgn-content'>
                  <div className='asgn-user-grid'>
                    {paginatedMembers.map((member) => {
                      const isChecked = selectedMemberIds.has(member.id);
                      const currentConfig = licenseConfig[member.license];
                      const hasCurrentLicense = member.license === selectedLicense;
                      const isManaged = Boolean(member.scimManaged);
                      return (
                        <div
                          key={member.id}
                          onClick={() => handleMemberToggle(member.id)}
                          className='asgn-user-card'
                          data-selected={isChecked || undefined}
                          data-managed={isManaged || undefined}
                          title={
                            isManaged
                              ? "This member's license is managed by their organization's identity provider and cannot be changed here."
                              : undefined
                          }
                        >
                          <div className='asgn-user-card-inner'>
                            <input
                              type='checkbox'
                              className='asgn-checkbox'
                              checked={isChecked}
                              disabled={isManaged}
                              onChange={() => handleMemberToggle(member.id)}
                            />
                            <div className='asgn-user-card-info'>
                              <p className='asgn-user-name'>{member.name}</p>
                              <p className='asgn-user-email'>{member.email}</p>
                              <div className='asgn-user-license'>
                                <span className={currentConfig.badgeClass}>
                                  <span
                                    className={`${currentConfig.dotClass} asgn-dot-sm`}
                                  />
                                  {currentConfig.label}
                                </span>
                                {hasCurrentLicense && (
                                  <span className='asgn-user-current'>
                                    (current)
                                  </span>
                                )}
                                {isManaged && (
                                  <span className='asgn-user-managed'>
                                    Managed by identity provider
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {totalPages > 1 && (
                    <div className='asgn-pagination'>
                      <span>
                        Page {currentPage} of {totalPages}
                      </span>
                      <div className='asgn-pagination-buttons'>
                        <button
                          className='asgn-btn asgn-btn-outline asgn-btn-sm'
                          disabled={currentPage === 1}
                          onClick={() => setCurrentPage((p) => p - 1)}
                        >
                          Previous
                        </button>
                        <button
                          className='asgn-btn asgn-btn-outline asgn-btn-sm'
                          disabled={currentPage === totalPages}
                          onClick={() => setCurrentPage((p) => p + 1)}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}

                  {(changes.add > 0 || changes.remove > 0) && (
                    <div className='asgn-apply-bar'>
                      <div className='asgn-apply-info'>
                        {changes.add > 0 && (
                          <span className='asgn-apply-add'>
                            +{changes.add} to assign
                          </span>
                        )}
                        {changes.remove > 0 && (
                          <span className='asgn-apply-remove'>
                            -{changes.remove} to remove
                          </span>
                        )}
                      </div>
                      <button
                        className='asgn-btn asgn-btn-primary'
                        onClick={handleApply}
                        disabled={isApplying}
                      >
                        {isApplying ? 'Applying...' : 'Apply Changes'}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      <div id='credits-section' />
      <CreditsUsageOverview
        cur_user={cur_user}
        onManageCredits={() => {
          history.push({ ...location, hash: '#put-bite-to-work' });
          handleTabChange('Subscriptions');
        }}
        canManageBilling={cur_user?.role === 'owner'}
      />
    </div>
  );
};

export default LicenseCentricAssigner;
